import * as d3 from 'd3';
import * as moment from 'moment';
import data from '../../../../data/data.json';
import { wordwrap } from 'd3-jetpack';
// import { apdate, apmonth, intcomma } from 'journalize';
// import _ from 'lodash';

// a reference to the default graphic container, change if needed
const container = d3.select('#shootings-mini-decade');

// a helper function to grab the container's width
const getFrameWidth = () => container.node().offsetWidth;

let chartHeight = 180,
  margin = {
    top: 30,
    bottom: 60,
  };

const yearsData = data['years'],
  shootingsData = data['shootings'];

let svg = container.append('svg').attr('class', 'shootings-mini-decade'),
  g = svg.append('g').attr('class', 'all-shootings-decade-group');

let formattedYears = yearsData.map(d => {
  d.year = moment(d.year, 'YYYY');
  return d;
});

let formattedShootings = shootingsData.map(d => {
  d.date = moment(d.date, 'YYYY-M-D');
  return d;
});

let dateRange = d3.extent(formattedYears, d => d.year);

let x = d3
  .scaleTime()
  .domain(dateRange)
  .nice();

// the maximum number across number of people killed or injured during mass shootings
let maxPeople = d3.max(formattedShootings, d => {
  return d.killed > d.injured ? d.killed : d.injured;
});

let sqrtScale = d3
  .scaleSqrt()
  .domain([0, maxPeople])
  .range([0, 40]); // this should be static, changing it based on distance between ticks on resize would confuse the reader

let shootingsGroup = g.append('g').attr('class', 'shootings-decade-group'),
  datesGroup = g.append('g').attr('class', 'dates-decade-group'),
  annotationsGroup = g.append('g').attr('class', 'annotations-decade-group');

let shootings = shootingsGroup
  .selectAll('.shooting')
  .data(formattedShootings)
  .enter()
  .append('g')
  .attr('class', 'shooting');

let circlesDate = datesGroup
  .selectAll('.circle-date')
  .data(formattedShootings)
  .enter()
  .append('circle')
  .attr('r', '3px')
  .attr('class', 'circle-date');

let annotations = annotationsGroup
  .selectAll('.annotation')
  .data(formattedShootings)
  .enter()
  .append('g')
  .attr('class', 'annotation');

let arc = d3.arc();
let circlesKilled = shootings
  .append('path')
  .attr('d', d =>
    arc({
      innerRadius: 0,
      outerRadius: sqrtScale(d.killed),
      startAngle: -Math.PI * 0.5,
      endAngle: Math.PI * 0.5,
    })
  )
  .attr('class', 'circle-killed');

let circlesInjured = shootings
  .append('path')
  .attr('d', d =>
    arc({
      innerRadius: 0,
      outerRadius: sqrtScale(d.injured),
      startAngle: -Math.PI * 0.5,
      endAngle: Math.PI * 0.5,
    })
  )
  .attr('class', 'circle-injured');

let xAxis = g
  .append('g')
  .attr('class', 'shootings-decade-axis axis axis--x')
  .attr('transform', `translate(0,${chartHeight})`);

let annotationLines = annotations
  .append('line')
  .attr('class', 'annotation-line');

let annotationText = annotations
  .append('text')
  .attr('class', 'annotation-name');

let labelText = g.append('g').attr('class', 'label-text label-decade-text'),
  killedLabel = labelText.append('text').text('Killed'),
  injuredLabel = labelText.append('text').text('Injured');

let formatYear = d3.timeFormat('%Y');

function customFormat(d) {
  if (moment(d, 'YYYY').year() === 2023) return '';
  else return formatYear(d);
}

function mobileCustomFormat(d) {
  let years = [2009, 2015, 2022];
  if (years.find(y => moment(d, 'YYYY').year() === y)) return formatYear(d);
  else return '';
}

function resize() {
  let frameWidth = getFrameWidth(),
    breakPoint = 664;

  if (frameWidth < breakPoint) {
    margin.top = 0;
    margin.right = 36;
    margin.left = 36;
  } else {
    margin.top = 30;
    margin.right = 65;
    margin.left = 45;
  }

  svg
    .attr('height', chartHeight + margin.top + margin.bottom)
    .attr('width', frameWidth);

  g.attr('transform', `translate(${margin.left},${margin.top})`);

  x.range([0, frameWidth - margin.left - margin.right]);

  xAxis.call(
    d3
      .axisBottom(x)
      .ticks(10)
      .tickFormat(frameWidth < breakPoint ? mobileCustomFormat : customFormat)
  );

  // d3.selectAll('.shootings-decade-axis .tick text').each(function() {
  //   let years = ['2009', '2022'];
  //   if (years.find(y => d3.select(this).text() === y))
  //     d3.select(this).attr('font-weight', 700);
  // });

  circlesKilled.attr(
    'transform',
    d => `translate(${x(d.date)},${chartHeight})`
  );

  circlesInjured.attr(
    'transform',
    d => `translate(${x(d.date)},${chartHeight}) rotate(180)`
  );

  circlesDate.attr('transform', d => `translate(${x(d.date)},${chartHeight})`);

  // handle annotation display for mobile and desktop
  annotations.attr('display', (d, i) => {
    if (frameWidth < breakPoint) {
      if (i === 0 || i === formattedShootings.length - 1) {
        return 'initial';
      } else {
        return 'none';
      }
    } else {
      return 'initial';
    }
  });

  let lineDistFromAxis = 75,
    lineDistFromAxisLonger = 150;

  let adjustedAnnotations = [];

  for (let i = 0; i < formattedShootings.length - 1; i++) {
    // if two annotations are less than 1 year apart, add them to array of annotations to be adjusted
    if (
      formattedShootings[i + 1].date.diff(
        formattedShootings[i].date,
        'months'
      ) < 12
    ) {
      adjustedAnnotations.push({
        laterShooting: formattedShootings[i + 1],
        earlierShooting: formattedShootings[i],
        monthsDiff: formattedShootings[i + 1].date.diff(
          formattedShootings[i].date,
          'months'
        ),
      });
    }
  }

  // if two mass shooting are within a year of eachother, stagger the annotations
  annotations.attr('transform', d => {
    if (adjustedAnnotations.find(a => a.laterShooting.id === d.id)) {
      return `translate(${x(d.date)},${chartHeight - lineDistFromAxis})`;
    } else if (adjustedAnnotations.find(a => a.earlierShooting.id === d.id)) {
      return `translate(${x(d.date)},${chartHeight - lineDistFromAxisLonger})`;
    } else {
      return `translate(${x(d.date)},${chartHeight - lineDistFromAxis})`;
    }
  });

  // handle line lengths for shootings within a year of eachother
  annotationLines.attr('y1', 0).attr('y2', d => {
    if (adjustedAnnotations.find(a => a.laterShooting.id === d.id)) {
      return lineDistFromAxis;
    } else if (adjustedAnnotations.find(a => a.earlierShooting.id === d.id)) {
      return lineDistFromAxisLonger;
    } else {
      return lineDistFromAxis;
    }
  });

  // handle text anchor for two shootings in the same month
  annotationText.attr('text-anchor', d => {
    if (adjustedAnnotations.find(a => a.laterShooting.id === d.id)) {
      if (
        adjustedAnnotations.find(a => a.laterShooting.id === d.id).monthsDiff <
        1
      ) {
        return frameWidth < breakPoint ? 'middle' : 'start';
      }
      return 'middle';
    } else {
      return 'middle';
    }
  });

  // wordwrap the mass shooting names
  annotationText.tspans(d => wordwrap(d.name + d['chart-asterisk'], 15));

  annotationText.each(function() {
    d3.select(this).attr('y', -this.getBoundingClientRect().height);
  });

  // this is easier if we go from earliest to latest shooting
  // because of the positioning of this will not change (first shooting is always the same)
  labelText.attr(
    'transform',
    `translate(${x(formattedShootings[0].date) -
      (frameWidth < breakPoint ? 45 : 80)},${chartHeight})`
  ); // take the first shooting's date to position

  killedLabel.attr('dy', frameWidth < breakPoint ? -30 : -10);
  injuredLabel.attr('dy', frameWidth < breakPoint ? 55 : 40);
}

// turn them back into numerical years BECAUSE SOMETHING IS MUTATING THIS DATA
yearsData.forEach(y => {
  y.year = moment(y.year, 'YYYY').year();
});

// call resize on load
resize();
window.addEventListener('resize', resize);
